import React, { useEffect, useState } from "react";
import "../Styling/EmployeeDistribution.css";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Users } from "lucide-react";

const EmployeeDistribution = ({ filters }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Prefer filters, fallback to logged-in user
  const orgId = filters?.orgId || user?.organisationId;
  const companyCode = Number(filters?.companyCode || user?.companyCode);
  const fiscalYear = filters?.fiscalYear || "All Fiscal Years";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showActiveBreakdown, setShowActiveBreakdown] = useState(false);

  useEffect(() => {
    if (!orgId || !companyCode) return;

    const fetchDistribution = async () => {
      try {
        setLoading(true);

        const params = new URLSearchParams({
          orgId,
          companyCode,
        });

        const res = await fetch(
          `${Base_URL}/Resource/employee-distribution?${params.toString()}`
        );

        if (!res.ok) {
          setData([]);
          return;
        }

        const json = await res.json();
        setData(Array.isArray(json) ? json : []);
      } catch (err) {
        console.error("Employee distribution error:", err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDistribution();
  }, [orgId, companyCode]);

  /* ================= Selected Year Data ================= */

  const selectedData =
    fiscalYear !== "All Fiscal Years"
      ? data.find((item) => item.year === Number(fiscalYear))
      : data[0];

  const totalActiveEmployees = selectedData?.totalActive || 0;
  const totalLeftEmployees = selectedData?.left || 0;

  const totalPermanentEmployees = selectedData?.permanent || 0;
  const totalDummyEmployees = selectedData?.dummy || 0;
  const totalPartialEmployees = selectedData?.partial || 0;
  const totalInternEmployees = selectedData?.intern || 0;

  const totalConfirmedEmployees = selectedData?.confirmed || 0;
  const totalProbationEmployees = selectedData?.probation || 0;

  /* ================= Loading ================= */

  if (loading) {
    return (
      <div className="employee-distribution-card compact">
        <div className="text-center text-muted py-3">
          Loading…
        </div>
      </div>
    );
  }

 return (
  <div className="ed-wrapper">
    <div className="ed-card">

      {/* Header */}
      <div className="ed-header">
        <div className="ed-header-left">
          <div className="ed-icon">
            <Users size={16} />
          </div>
          <h6>
            Employee Distribution
            <span className="ed-fy-label">
              {fiscalYear === "All Fiscal Years"
                ? "All Years"
                : fiscalYear}
            </span>
          </h6>
        </div>

        <span
          className="ed-view-all"
          onClick={() => navigate("/resourceList")}
        >
          View all
        </span>
      </div>

      {/* Hint text */}
      <p className="ed-hint">
        Click on Active Employees to view full breakdown
      </p>

      {/* Tiles */}
      <div className="row g-2">
        <DistributionStat
          label="Active Employees"
          value={totalActiveEmployees}
          type="Active"
          onClick={() => setShowActiveBreakdown(true)}
        />

        <DistributionStat
          label="Left Employees"
          value={totalLeftEmployees}
          type="Left"
        />
      </div>

    </div>

    {/* Modal stays outside card but inside wrapper */}
    {showActiveBreakdown && (
      <div className="ed-modal-overlay">
        <div className="ed-modal">
          <div className="ed-modal-header">
            <h6>Active Employee Breakdown</h6>
            <button
              className="ed-close-btn"
              onClick={() => setShowActiveBreakdown(false)}
            >
              ✕
            </button>
          </div>

          <div className="ed-modal-section">
            <p className="ed-modal-title">Employee Type</p>
            <div className="row g-2">
              <DistributionStat label="Permanent" value={totalPermanentEmployees} type="Permanent" />
              <DistributionStat label="Dummy" value={totalDummyEmployees} type="Dummy" />
              <DistributionStat label="Partial" value={totalPartialEmployees} type="Partial" />
              <DistributionStat label="Intern" value={totalInternEmployees} type="Intern" />
            </div>
          </div>

          <div className="ed-modal-section">
            <p className="ed-modal-title">Status Reason</p>
            <div className="row g-2">
              <DistributionStat label="Confirmed" value={totalConfirmedEmployees} type="Confirmed" />
              <DistributionStat label="Probation" value={totalProbationEmployees} type="Probation" />
            </div>
          </div>

        </div>
      </div>
    )}

  </div>
);
};

/* ================= Distribution Tile Component ================= */

const DistributionStat = ({ label, value, type, onClick }) => {
  const colorMap = {
    Active: "#93C5FD",
    Left: "#FCA5A5",
    Permanent: "#BFDBFE",
    Dummy: "#FECACA",
    Partial: "#FDE68A",
    Intern: "#C6F6D5",
    Confirmed: "#BBF7D0",
    Probation: "#FDE68A",
  };

  return (
    <div className="col-6 col-md-3">
      <div
        className="distribution-stat compact"
        style={{
          backgroundColor: colorMap[type] || "#E5E7EB",
          color: "#1f2937",
          cursor: onClick ? "pointer" : "default",
        }}
        onClick={onClick}
      >
        <h5>{value}</h5>
        <span>{label}</span>
      </div>
    </div>
  );
};

export default EmployeeDistribution;